import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './ChangeRequestParticipantList.module.scss';
import { ChangeRequestParticipantInfo, ChangeRequestAttachment, ChangedItem } from '@/types';
import { CardSection, ExpandableCard, ExpandableCardHandle, NoResultsCard } from '@/components/Cards';
import { Button } from '@ygtechnologies/rubus-ui';
import { ButtonBar } from '@/components/Buttons';
import { ProfileIcon } from '@/components/Icons';
import { faFileInvoiceDollar } from '@fortawesome/pro-light-svg-icons';
import { getChangeRequestParticipantFees, saveChangeRequestParticipantFees } from '@/services/change-requests';
import { FeeEditDialog, PriceDisplay } from '@/seller/components/fees';
import { sumBy } from 'lodash-es';
import ChangeRequestAttachmentList from '../ChangeRequestAttachmentList';
import Indicator from '@/components/Indicator';
import { DateTimeDisplay } from '@/components/localization';
import { ChangeRequestParticipantOfferLines } from '../ChangeRequestParticipantOfferLines';
import { ProjectPermissionToggle } from '@/components/permissions';
import ReadyToggleCard from '@/components/ReadyToggleCard';

interface ChangeRequestParticipantListItemProps {
    participant: ChangeRequestParticipantInfo;
    onGiveOfferClick?(participant: ChangeRequestParticipantInfo): void;
    onReadyStateChange?(participant: ChangeRequestParticipantInfo, newValue: boolean): void;
    onAttachmentChange?(participant: ChangeRequestParticipantInfo, changes: ChangedItem<ChangeRequestAttachment>): void;
    onParticipantChangeClick?(participant: ChangeRequestParticipantInfo): void;
    readOnly: boolean;
}

const ChangeRequestParticipantListItem: React.FC<ChangeRequestParticipantListItemProps> = ({
    participant,
    onGiveOfferClick,
    onReadyStateChange,
    onAttachmentChange,
    onParticipantChangeClick,
    readOnly,
}) => {
    const [showFeeDialog, setShowFeeDialog] = useState(false);
    const [expanded, setExpanded] = useState(participant.isCurrentUser);

    const canChangeOffer = participant.permissions.canChangeOffer && !readOnly;

    const renderChangeButtons = () => {
        const hasOffer = participant.offerPrice != null;

        if (!canChangeOffer || !onGiveOfferClick) {
            return null;
        }

        return (
            <div>
                <ButtonBar className={theme.buttons}>
                    <ProjectPermissionToggle condition={'isAdministrator'}>
                        {onParticipantChangeClick && (
                            <Button onClick={() => onParticipantChangeClick(participant)} variant="tertiary">
                                <Trans>Change participant user</Trans>
                            </Button>
                        )}
                    </ProjectPermissionToggle>
                    <Button
                        variant="secondary"
                        icon={faFileInvoiceDollar}
                        onClick={(e) => triggerHandler(onGiveOfferClick, participant)}
                        disabled={participant.isReadyToSend}>
                        {hasOffer ? <Trans>Update quote</Trans> : <Trans>Give quote</Trans>}
                    </Button>
                </ButtonBar>
                {canChangeOffer && (
                    <ReadyToggleCard
                        checked={participant.isReadyToSend}
                        onChange={(checked) => triggerHandler(onReadyStateChange, participant, checked)}
                    />
                )}
            </div>
        );
    };

    const renderTotalPrice = () => {
        return (
            <PriceDisplay
                value={{
                    basePrice: sumBy(participant.offerLines, (x) => x.priceCalculation!.basePrice),
                    finalPrice: sumBy(participant.offerLines, (x) => x.priceCalculation!.finalPrice),
                }}
                redacted={participant.priceRedacted}
            />
        );
    };

    const handleAttachmentChange = (change: ChangedItem<ChangeRequestAttachment>) => {
        triggerHandler(onAttachmentChange, participant, change);
    };

    const renderExpandableContent = () => {
        const hasNoOffer = !participant.offerNote && !participant.hasLines && !participant.isReadyToSend;

        return (
            <div className={theme.expand}>
                {renderChangeButtons()}

                {hasNoOffer && (
                    <NoResultsCard className={theme.noOffer}>
                        {participant.isCurrentUser ? (
                            <Trans>You have not yet provided a offer for this change request</Trans>
                        ) : (
                            <Trans>{participant.displayName} has not yet provided a offer for this change request</Trans>
                        )}
                    </NoResultsCard>
                )}

                {participant.offerNote && (
                    <div className={theme.note}>
                        <h6>
                            <Trans>Offer message</Trans>
                        </h6>
                        <div>{participant.offerNote}</div>
                    </div>
                )}
                <ChangeRequestParticipantOfferLines
                    priceIsRedacted={participant.priceRedacted}
                    offerLines={participant.offerLines}
                />

                <div className={theme.offerDetails}>
                    {participant.offerAttachments.length > 0 && (
                        <div className={theme.attachments}>
                            <h6>
                                <Trans>Attachments</Trans>
                            </h6>
                            <ChangeRequestAttachmentList
                                value={participant.offerAttachments}
                                canAdd={false}
                                canEdit={!readOnly && participant.permissions.canModifyAttachmentFlags}
                                canDelete={false}
                                showVisibilityFlags={true}
                                ownerProfileId={participant.profileId}
                                itemsPerRow={3}
                                onItemChange={handleAttachmentChange}
                                editMode="context"
                            />
                        </div>
                    )}
                </div>

                <FeeEditDialog
                    show={showFeeDialog}
                    loader={() => getChangeRequestParticipantFees(participant.id)}
                    onAccept={(data) => saveChangeRequestParticipantFees(participant.id, data)}
                    onClose={() => setShowFeeDialog(false)}
                    basePrice={participant.offerPrice || 0}
                    quantity={1}
                />
            </div>
        );
    };

    return (
        <ExpandableCard
            variant="flat"
            className={theme.participant}
            expanded={expanded}
            expandableContent={renderExpandableContent()}
            type="inside-bordered">
            <div className={theme.name}>
                <ProfileIcon profileId={participant.profileId} sizeInPx={32} />
                {/* <UserIcon userId={participant.userId} sizeInPx={32} invert /> */}
                <span>
                    {participant.displayName} <small>{participant.profileName}</small>
                </span>
            </div>

            <CardSection caption={<Trans>Modified</Trans>} width="200px" right>
                {participant.lastModified ? (
                    <DateTimeDisplay value={participant.lastModified} />
                ) : (
                    <span className="text-missing">
                        <Trans>N/A</Trans>
                    </span>
                )}
            </CardSection>

            <CardSection caption={<Trans>Quote status</Trans>} width="120px" right>
                {participant.isReadyToSend ? (
                    <Indicator color="success">
                        <Trans>Ready to send</Trans>
                    </Indicator>
                ) : (
                    <Indicator color="danger">
                        <Trans>Not ready</Trans>
                    </Indicator>
                )}
            </CardSection>

            <CardSection className="text-right" caption={<Trans>Subtotal</Trans>} width="120px" right>
                {renderTotalPrice()}
            </CardSection>

            <ExpandableCardHandle onExpandToggle={setExpanded} expanded={expanded} />
        </ExpandableCard>
    );
};

export default ChangeRequestParticipantListItem;
